import React, { forwardRef } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import type { TooltipContentProps } from 'recharts';
import { TreasuryBillDetail } from '@typeDefs/domain/assets';
import { formatCurrency, formatDate } from '@utils/formatters';
import { readToken } from '@utils/readToken';
import {
  buildValueHistorySeries,
  type ValueHistoryPoint,
  type ValueEventKind,
  type ValueHistorySeries,
} from '@utils/valueHistorySeries';
import styles from './ValueHistoryChart.module.css';

export interface IValueHistoryChartProps extends React.HTMLAttributes<HTMLDivElement> {
  treasuryBillDetail: TreasuryBillDetail;
}

interface ChartPalette {
  held: string;
  pending: string;
  drop: string;
  grid: string;
  axis: string;
}

function readPalette(): ChartPalette {
  return {
    held: readToken('--chart-held', '#f7fc68'),
    pending: readToken('--chart-pending', '#f2b007'),
    drop: readToken('--chart-drop', '#ff6b6b'),
    grid: readToken('--chart-grid', '#3a3c3e'),
    axis: readToken('--chart-axis', '#8a8c92'),
  };
}

interface ChartRow {
  time: number;
  held?: number;
  projected?: number;
  pending?: number;
  eventKind?: ValueEventKind;
  label?: string;
}

interface DropSegment {
  time: number;
  pre: number;
  post: number;
}

function toDropSegments(points: ValueHistoryPoint[]): DropSegment[] {
  const preByTime = new Map<number, number>();
  const postByTime = new Map<number, number>();
  for (const p of points) {
    const t = p.date.getTime();
    if (p.eventKind === 'saleRequested') preByTime.set(t, p.value);
    else if (p.eventKind === 'saleComplete') postByTime.set(t, p.value);
  }
  const segments: DropSegment[] = [];
  for (const [time, pre] of preByTime) {
    const post = postByTime.get(time);
    if (post !== undefined) segments.push({ time, pre, post });
  }
  return segments.sort((a, b) => a.time - b.time);
}

function toChartRows(points: ValueHistoryPoint[], pending: ValueHistoryPoint[]): ChartRow[] {
  const byKey = new Map<string, ChartRow>();
  // Fork points share a time; separate pre/post rows by eventKind so both survive.
  const keyOf = (p: ValueHistoryPoint) => `${p.date.getTime()}:${p.eventKind ?? p.segment}`;

  const upsert = (p: ValueHistoryPoint): ChartRow => {
    const k = keyOf(p);
    const existing = byKey.get(k);
    if (existing) return existing;
    const row: ChartRow = { time: p.date.getTime() };
    byKey.set(k, row);
    return row;
  };

  for (const p of points) {
    const row = upsert(p);
    if (p.segment === 'projected') row.projected = p.value;
    else row.held = p.value;
    if (p.eventKind) row.eventKind = p.eventKind;
    if (p.label) row.label = p.label;
  }
  for (const p of pending) {
    const row: ChartRow = {
      time: p.date.getTime(),
      pending: p.value,
      label: p.label,
      eventKind: p.eventKind ?? 'saleRequested',
    };
    byKey.set(`${p.date.getTime()}:pending:${row.pending}`, row);
  }
  return Array.from(byKey.values()).sort((a, b) => a.time - b.time);
}

const PILL: Record<ValueEventKind, string> = {
  purchase: styles.pillAccent,
  now: styles.pillAccent,
  maturity: styles.pillAccent,
  saleRequested: styles.pillAmber,
  saleComplete: styles.pillGreen,
};

function ValueTooltip({ active, payload, label }: TooltipContentProps) {
  if (!active || !payload || payload.length === 0) return null;
  const time = typeof label === 'number' ? label : Number(label);
  const row = (payload[0]?.payload ?? {}) as ChartRow;
  const value = Number(payload[0]?.value);
  return (
    <div className={styles.card}>
      {row.label && row.eventKind && (
        <span className={`${styles.pill} ${PILL[row.eventKind]}`}>{row.label}</span>
      )}
      {/* KNOWN, DEFERRED (preservoir-ai#82, D-12): the chart's date handling was not
          reviewed as part of the display migration. It renders via the shared formatDate,
          so it now resolves the London day rather than the browser's — consistent with
          TreasuryBillInformation's Purchase Date row, which reads the same
          orderDateTimeUtc. What is still deferred is whether a chart axis/tooltip wants a
          day at all, or a time; revisit when the chart is reinstated (it is not currently
          rendered — see TreasuryBillInformation.tsx). */}
      <div className={styles.cardMeta}>{formatDate(new Date(time))}</div>
      <div className={styles.cardValue}>{formatCurrency(value)}</div>
    </div>
  );
}

function eventDot(
  props: { payload?: unknown; cx?: number; cy?: number },
  palette: ChartPalette,
): React.ReactElement {
  const row = props.payload as ChartRow;
  const key = `dot-${row.time}-${row.eventKind ?? ''}`;
  if (!row.eventKind || row.eventKind === 'now') {
    return <circle key={key} r={0} cx={props.cx} cy={props.cy} fill="none" />;
  }
  return (
    <circle
      key={key}
      cx={props.cx}
      cy={props.cy}
      r={4}
      fill={palette.held}
      stroke={palette.grid}
      strokeWidth={1}
    />
  );
}

const PLACEHOLDER_TEXT: Record<Exclude<ValueHistorySeries['placeholder'], null>, string> = {
  'pending-acceptance': 'Pending acceptance — no value until accepted',
  rejected: 'Order declined — no holding',
};

const ValueHistoryChart = forwardRef<HTMLDivElement, IValueHistoryChartProps>(
  ({ treasuryBillDetail, className, ...rest }, ref) => {
    const series = buildValueHistorySeries(treasuryBillDetail);
    const rootClassName = [styles.chart, className].filter(Boolean).join(' ');

    if (series.placeholder) {
      const text = PLACEHOLDER_TEXT[series.placeholder];
      return (
        <div ref={ref} className={rootClassName} {...rest}>
          <div className={styles.placeholder} role="status">
            {text}
          </div>
        </div>
      );
    }

    const palette = readPalette();
    const rows = toChartRows(series.points, series.pendingSegment);
    const drops = toDropSegments(series.points);

    return (
      <div ref={ref} className={rootClassName} {...rest}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 8, left: 8 }}>
            <CartesianGrid stroke={palette.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="time"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              // KNOWN, DEFERRED (preservoir-ai#82, D-12): London-resolved via the shared
              // formatDate; the day-vs-time question is deferred with the tooltip above.
              tickFormatter={(t: number) => formatDate(new Date(t))}
              stroke={palette.axis}
              tick={{ fontSize: 11 }}
              minTickGap={24}
            />
            <YAxis
              stroke={palette.axis}
              tick={{ fontSize: 11 }}
              tickFormatter={(v: number) => formatCurrency(v)}
              width={72}
              domain={[series.yDomain.min, series.yDomain.max]}
              allowDataOverflow
            />
            <Tooltip content={ValueTooltip} />
            <Line
              type="linear"
              dataKey="held"
              stroke={palette.held}
              strokeWidth={2.5}
              connectNulls
              dot={(props) => eventDot(props, palette)}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            {!series.terminal && (
              <Line
                type="linear"
                dataKey="projected"
                stroke={palette.held}
                strokeOpacity={0.5}
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {drops.map((d) => (
              <ReferenceLine
                key={`drop-${d.time}`}
                segment={[
                  { x: d.time, y: d.pre },
                  { x: d.time, y: d.post },
                ]}
                stroke={palette.drop}
                strokeWidth={2.5}
                ifOverflow="visible"
              />
            ))}
            {series.hasPendingSegment && (
              <Line
                type="linear"
                dataKey="pending"
                stroke={palette.pending}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

ValueHistoryChart.displayName = 'ValueHistoryChart';

export default ValueHistoryChart;
